import React from 'react'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  IconButton
} from '@mui/material'
import {
  Science as ScienceIcon,
  Timeline as TimelineIcon,
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material'
import { formatAmount } from '../../utils/chemicalCalculations'
import { formatRelativeDate, getCategoryColor } from '../../utils/constants'
import StatusBadge from './StatusBadge'

const UnifiedTimeline = ({ 
  items, 
  onEditMeasurement, 
  onDeleteMeasurement,
  onEditAddition,
  onDeleteAddition,
  showActions = true 
}) => {

  const formatValue = (value) => {
    if (value === null || value === undefined) return '—'
    return typeof value === 'number' ? value.toFixed(1) : value
  }

  const renderMeasurement = (measurement) => (
    <Card key={measurement.id} variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <TimelineIcon sx={{ mr: 1, color: 'primary.main', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Water Test
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formatRelativeDate(measurement.measured_at)}
            </Typography>
            
            {/* Key parameters display */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {[
                { key: 'total_chlorine', label: 'TC', unit: 'ppm', calculate: (m) => (m.free_chlorine || 0) + (m.combined_chlorine || 0) },
                { key: 'ph', label: 'pH', unit: '' },
                { key: 'total_alkalinity', label: 'TA', unit: 'ppm' },
                { key: 'cya_stabilizer', label: 'CYA', unit: 'ppm' }
              ].map(param => {
                const value = param.calculate ? param.calculate(measurement) : measurement[param.key]
                return (
                  <Box key={param.key} sx={{ textAlign: 'center', minWidth: 60 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block' }}>
                      {param.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatValue(value)}{param.unit && value !== null ? param.unit : ''}
                    </Typography>
                  </Box>
                )
              })}
            </Box>
            
            {/* Notes display */}
            {measurement.notes && (
              <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1, borderLeft: '3px solid', borderColor: 'primary.main' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', fontWeight: 600, display: 'block', mb: 0.5 }}>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ fontSize: '0.85rem', fontStyle: 'italic' }}>
                  {measurement.notes}
                </Typography>
              </Box>
            )}
          </Box>
          
          {showActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => onEditMeasurement(measurement)}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => onDeleteMeasurement(measurement.id)}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )

  const renderChemicalAddition = (addition) => (
    <Card key={addition.id} variant="outlined">
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1 }}>
              <ScienceIcon sx={{ mr: 1, color: 'secondary.main', fontSize: 20 }} />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Chemical Addition
              </Typography>
              <StatusBadge
                label={addition.chemical_types?.category || 'custom'}
                color={getCategoryColor(addition.chemical_types?.category)}
                size="small"
                sx={{ textTransform: 'capitalize', fontSize: '0.7rem' }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {formatRelativeDate(addition.added_at)}
            </Typography>
            
            <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
              {addition.chemical_types?.name || 'Unknown Chemical'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Amount: {formatAmount(addition.amount, addition.unit)}
              {addition.chemical_types?.concentration_percent && (
                <Typography component="span" sx={{ ml: 1 }}>
                  ({addition.chemical_types.concentration_percent}% concentration)
                </Typography>
              )}
            </Typography>
            
            {addition.notes && (
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'grey.200'
                }}
              >
                <Typography variant="body2" color="text.primary">
                  <Typography component="span" sx={{ fontWeight: 600 }}>
                    Notes:
                  </Typography>{' '}
                  {addition.notes}
                </Typography>
              </Box>
            )}
          </Box>
          
          {showActions && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <IconButton
                onClick={() => onEditAddition(addition)}
                size="small"
                sx={{ color: 'text.secondary' }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                onClick={() => onDeleteAddition(addition.id)}
                size="small"
                sx={{ 
                  color: 'text.secondary',
                  '&:hover': { color: 'error.main' }
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )

  if (!items || items.length === 0) {
    return (
      <Card>
        <CardContent sx={{ textAlign: 'center', py: 4 }}>
          <TimelineIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No activity yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start by adding a measurement or chemical addition.
          </Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <Stack spacing={2}>
      {items.map((item) => {
        // Use the type property to determine rendering
        if (item.type === 'measurement') {
          return renderMeasurement(item)
        } else if (item.type === 'addition') {
          return renderChemicalAddition(item)
        }
        return null
      })}
    </Stack>
  )
}

export default UnifiedTimeline